import type { Selection } from "@ui/components/AvatarPicker";
import { useCallback, useRef, useState } from "react";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import { avatarKindFromSelection, selectionFromAvatarKind } from "@/lib/avatar/avatar-kind";

export interface AvatarKindFields {
  avatarKind: string;
  signedId: string;
}

export interface UseAvatarKindFieldOptions {
  /** Server-provided `avatar_kind` the picker starts on. */
  initialAvatarKind: string;
  /** Server-provided avatar URL, shown as the upload preview when the kind is "custom". */
  initialAvatarUrl?: string | null;
  /** Full-snapshot sink — write both fields into the page's form on every change. */
  onFieldsChange: (fields: AvatarKindFields) => void;
}

export interface UseAvatarKindFieldResult {
  selection: Selection;
  previewUrl: string | null;
  onSelectionChange: (selection: Selection) => void;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
}

/**
 * AvatarPicker wired to the backend's `avatar_kind` + `signed_id` pair, as used by the
 * account-creator picture stages. Layers the kind<->Selection mapping and the "leaving the upload
 * slot discards the pending file" rule on top of `useImageUploadField`; a failed upload resets the
 * picker to the default tile.
 */
export function useAvatarKindField({
  initialAvatarKind,
  initialAvatarUrl,
  onFieldsChange,
}: UseAvatarKindFieldOptions): UseAvatarKindFieldResult {
  const [selection, setSelection] = useState<Selection>(() => selectionFromAvatarKind(initialAvatarKind));
  const signedIdRef = useRef("");
  const onFieldsChangeRef = useRef(onFieldsChange);
  onFieldsChangeRef.current = onFieldsChange;

  const resetToDefault = useCallback(() => {
    signedIdRef.current = "";
    setSelection({ type: "default" });
    onFieldsChangeRef.current({ avatarKind: "default", signedId: "" });
  }, []);

  // The base hook resets a failed upload's preview to the initial image; here a
  // failure must leave the upload tile visibly empty (the picker falls back to
  // the default tile), so the failure can't masquerade as a staged photo.
  const setPreviewRef = useRef<(url: string | null) => void>(() => {});

  const field = useImageUploadField({
    initialPreviewUrl: initialAvatarKind === "custom" ? (initialAvatarUrl ?? null) : null,
    onUploaded: (signedId) => {
      signedIdRef.current = signedId;
      onFieldsChangeRef.current({ avatarKind: "custom", signedId });
    },
    onUploadError: () => {
      setPreviewRef.current(null);
      resetToDefault();
    },
    onRemoved: resetToDefault,
  });
  const { setPreview, onFileSelect: uploadFile } = field;
  setPreviewRef.current = setPreview;

  const onSelectionChange = useCallback(
    (next: Selection) => {
      setSelection(next);
      if (next.type !== "upload") {
        // Leaving the upload slot discards any pending file + its preview.
        setPreview(null);
        signedIdRef.current = "";
      }
      onFieldsChangeRef.current({ avatarKind: avatarKindFromSelection(next), signedId: signedIdRef.current });
    },
    [setPreview],
  );

  const onFileSelect = useCallback(
    (file: File) => {
      setSelection({ type: "upload" });
      onFieldsChangeRef.current({ avatarKind: "custom", signedId: signedIdRef.current });
      uploadFile(file);
    },
    [uploadFile],
  );

  return {
    selection,
    previewUrl: field.previewUrl,
    onSelectionChange,
    onFileSelect,
    onRemove: field.onRemove,
  };
}
